import {Component, forwardRef, Input} from '@angular/core';
import {CacheDataService} from '../../api/services/cache-data.service';
import {FlatTreeControl} from '@angular/cdk/tree';
import {TreeViewSelectionNode} from './helpers/tree-view-selection-node';
import {TreeViewDataSource} from './helpers/tree-vew-data-source';
import {FilterEditorComponent} from './filter-editor/filter-editor.component';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {CleanLocation} from '../../api/models/clean-location';
import {LocationKey} from '../../api/models/location-key';
import {AttributeKey} from '../../api/models/attribute-key';
import {Attribute_TaxonomyAttribute} from '../../api/models/attribute-_taxonomy-attribute';

@Component({
  selector: "attribute-taxonomy-filter-editor",
  templateUrl: "./filter-editor/filter-editor.component.html",
  styleUrls: ["./filter-editor/filter-editor.component.css"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AttributeTaxonomyFilterEditorComponent),
      multi: true
    }
  ]
})
export class AttributeTaxonomyFilterEditorComponent extends FilterEditorComponent<CleanLocation, LocationKey, TVSNAttributeTaxonomy, AttributeTaxonomyDataSource> implements ControlValueAccessor {

  constructor(cacheDataService: CacheDataService) {
    super(cacheDataService);
  }

  protected getNewDataSource(treeControl: FlatTreeControl<TVSNAttributeTaxonomy, TVSNAttributeTaxonomy>, cacheDataService: CacheDataService): AttributeTaxonomyDataSource {
    return new AttributeTaxonomyDataSource(treeControl, cacheDataService);
  }
}


export class AttributeTaxonomyDataSource extends TreeViewDataSource<Attribute_TaxonomyAttribute, AttributeKey, TVSNAttributeTaxonomy> {

  constructor(
    treeControl: FlatTreeControl<TVSNAttributeTaxonomy>,
    public cacheDataService: CacheDataService) {
    super(treeControl);
  }

  protected loadInitialData(callback: (results: TVSNAttributeTaxonomy[]) => void) {
    this.cacheDataService.cacheDataGetAttributeTaxonomyGroups({ pageNumber: 1, pageSize: 1000 }).subscribe(result => {
      const nodes = result.page.map(item => new TVSNAttributeTaxonomy(
        [], this.blankCover(item.taxonomyGroup), 1,
        {
          taxonomyGroup: item.taxonomyGroup
        },
        undefined, true, true
      ));
      callback(nodes);
    });
  }

  protected loadChildNodes(node: TVSNAttributeTaxonomy, callback: (results: TVSNAttributeTaxonomy[]) => void) {
    if (node.level === 1) {
      this.cacheDataService.cacheDataGetAttributeTaxonomySubGroupsByTaxonomyGroup({ taxonomyGroup: node.sourceValue.taxonomyGroup, pageNumber: 1, pageSize: 1000 }).subscribe(result => {
        const nodes : TVSNAttributeTaxonomy[] = result.page.map(item => new TVSNAttributeTaxonomy(
          [], this.blankCover(item.taxonomySubGroup), node.level + 1,
          {
            taxonomyGroup: item.taxonomyGroup,
            taxonomySubGroup: item.taxonomySubGroup
          },
          node, true, true
        ));
        callback(nodes);
      });
    } else if (node.level === 2) {
      this.cacheDataService.cacheDataGetAttributeTaxonomyAttributesByTaxonomyGroupAndTaxonomySubGroup({ taxonomyGroup: node.sourceValue.taxonomyGroup, taxonomySubGroup: node.sourceValue.taxonomySubGroup, pageNumber: 1, pageSize: 1000 }).subscribe(result => {
        const nodes: TVSNAttributeTaxonomy[] = result.page.map(item => new TVSNAttributeTaxonomy(
          [], this.blankCover(item.category), node.level + 1,
          {
            key: item.key,
            taxonomyGroup: item.taxonomyGroup,
            taxonomySubGroup: item.taxonomySubGroup,
            category: item.category
          },
          node, false, true
        ));
        callback(nodes);
      });
    }
  }
}

export class TVSNAttributeTaxonomy extends TreeViewSelectionNode<Attribute_TaxonomyAttribute, AttributeKey> {

  protected deriveKey(): AttributeKey {
    return {
      key: this.sourceValue.key,
      displayName: this.sourceValue.category,
      taxonomyGroup: this.sourceValue.taxonomyGroup,
      taxonomySubGroup: this.sourceValue.taxonomySubGroup
    };
  }

  protected isAnyKnownChildSelected(allKnownSelections: AttributeKey[]): boolean {
    // Rules:
    // If city != null, there can be no children
    // If current level matches, it must NOT match entirely (i.e, there target should have a lower level than THIS node

    if (!this.sourceValue.key) {
      if (this.sourceValue.taxonomySubGroup) {
        return allKnownSelections?.some(x => x.taxonomySubGroup == this.sourceValue.taxonomySubGroup && x.key);
      } else if (this.sourceValue.taxonomyGroup) {
        return allKnownSelections?.some(x => x.taxonomyGroup === this.sourceValue.taxonomyGroup && x.taxonomySubGroup);
      }
    }

    return false;
  }

  public isKeyForMe(key: AttributeKey): boolean {
    return key.key == this.sourceValue.key
    && key.taxonomySubGroup === this.sourceValue.taxonomySubGroup
    && key.taxonomyGroup === this.sourceValue.taxonomyGroup;
  }

  public isChildOf(key: AttributeKey): boolean {
    if (key.taxonomySubGroup != null) {
      return key.taxonomyGroup === this.sourceValue.taxonomyGroup
        && key.taxonomySubGroup === this.sourceValue.taxonomySubGroup;
    } else if (key.taxonomyGroup != null) {
      return key.taxonomyGroup === this.sourceValue.taxonomyGroup;
    }

    return false;
  }

  public toSourceKey(): AttributeKey {
    return {
      key: this.sourceValue.key,
      displayName: this.sourceValue.category,
      taxonomyGroup: this.sourceValue.taxonomyGroup,
      taxonomySubGroup: this.sourceValue.taxonomySubGroup
    }
  }

  public matchesKey(key: AttributeKey): boolean {
    return key.key === this.sourceValue.key
    && key.taxonomySubGroup === this.sourceValue.taxonomySubGroup
    && key.taxonomyGroup === this.sourceValue.taxonomyGroup;
  }

}
